import manifestJson from '../../data-model/instagram-eignungstest-datenmodell/00-manifest.json';
import appConfigJson from '../../data-model/instagram-eignungstest-datenmodell/01-app-config.json';
import clustersJson from '../../data-model/instagram-eignungstest-datenmodell/02-clusters.json';
import questionsJson from '../../data-model/instagram-eignungstest-datenmodell/03-questions.json';
import clusterMatrixJson from '../../data-model/instagram-eignungstest-datenmodell/04-cluster-matrix.json';
import contextQuestionsJson from '../../data-model/instagram-eignungstest-datenmodell/05-context-questions.json';
import resultBandsJson from '../../data-model/instagram-eignungstest-datenmodell/06-result-bands.json';
import tensionRulesJson from '../../data-model/instagram-eignungstest-datenmodell/07-tension-rules.json';
import recommendationRulesJson from '../../data-model/instagram-eignungstest-datenmodell/08-recommendation-rules.json';
import glossaryJson from '../../data-model/instagram-eignungstest-datenmodell/09-glossary.json';

import {
  appConfigSchema,
  clusterMatrixSchema,
  clustersSchema,
  contextQuestionsSchema,
  glossarySchema,
  manifestSchema,
  questionsSchema,
  recommendationRulesFileSchema,
  resultBandsSchema,
  tensionRulesSchema,
} from './schemas';

/**
 * Lädt und validiert das gesamte fachliche Datenmodell (DAT-002).
 * Wird sowohl zur Laufzeit im Browser als auch im Build-Validierungsskript verwendet.
 */
export interface DataModelValidationError {
  file: string;
  message: string;
}

export interface DataModel {
  manifest: ReturnType<typeof manifestSchema.parse>;
  appConfig: ReturnType<typeof appConfigSchema.parse>;
  clusters: ReturnType<typeof clustersSchema.parse>;
  questions: ReturnType<typeof questionsSchema.parse>;
  clusterMatrix: ReturnType<typeof clusterMatrixSchema.parse>;
  contextQuestions: ReturnType<typeof contextQuestionsSchema.parse>;
  resultBands: ReturnType<typeof resultBandsSchema.parse>;
  tensionRules: ReturnType<typeof tensionRulesSchema.parse>;
  recommendationRules: ReturnType<typeof recommendationRulesFileSchema.parse>;
  glossary: ReturnType<typeof glossarySchema.parse>;
}

function parseFile<T>(
  file: string,
  schema: { safeParse: (input: unknown) => { success: boolean; data?: T; error?: unknown } },
  data: unknown,
  errors: DataModelValidationError[],
): T | undefined {
  const result = schema.safeParse(data);
  if (!result.success) {
    const message =
      result.error instanceof Error ? result.error.message : JSON.stringify(result.error);
    errors.push({ file, message });
    return undefined;
  }
  return result.data;
}

export function parseDataModel(): { data?: DataModel; errors: DataModelValidationError[] } {
  const errors: DataModelValidationError[] = [];

  const manifest = parseFile('00-manifest.json', manifestSchema, manifestJson, errors);
  const appConfig = parseFile('01-app-config.json', appConfigSchema, appConfigJson, errors);
  const clusters = parseFile('02-clusters.json', clustersSchema, clustersJson, errors);
  const questions = parseFile('03-questions.json', questionsSchema, questionsJson, errors);
  const clusterMatrix = parseFile(
    '04-cluster-matrix.json',
    clusterMatrixSchema,
    clusterMatrixJson,
    errors,
  );
  const contextQuestions = parseFile(
    '05-context-questions.json',
    contextQuestionsSchema,
    contextQuestionsJson,
    errors,
  );
  const resultBands = parseFile('06-result-bands.json', resultBandsSchema, resultBandsJson, errors);
  const tensionRules = parseFile(
    '07-tension-rules.json',
    tensionRulesSchema,
    tensionRulesJson,
    errors,
  );
  const recommendationRules = parseFile(
    '08-recommendation-rules.json',
    recommendationRulesFileSchema,
    recommendationRulesJson,
    errors,
  );
  const glossary = parseFile('09-glossary.json', glossarySchema, glossaryJson, errors);

  if (
    !manifest ||
    !appConfig ||
    !clusters ||
    !questions ||
    !clusterMatrix ||
    !contextQuestions ||
    !resultBands ||
    !tensionRules ||
    !recommendationRules ||
    !glossary
  ) {
    return { errors };
  }

  return {
    data: {
      manifest,
      appConfig,
      clusters,
      questions,
      clusterMatrix,
      contextQuestions,
      resultBands,
      tensionRules,
      recommendationRules,
      glossary,
    },
    errors: [],
  };
}

let cachedDataModel: DataModel | undefined;

/** Liefert das validierte Datenmodell oder wirft mit den gesammelten Fehlern (DAT-002). */
export function getDataModel(): DataModel {
  if (cachedDataModel) return cachedDataModel;
  const { data, errors } = parseDataModel();
  if (!data) {
    if (import.meta.env?.DEV) {
      console.error('Datenmodell-Validierung fehlgeschlagen:', errors);
    }
    throw new DataModelError(errors);
  }
  cachedDataModel = data;
  return data;
}

export class DataModelError extends Error {
  readonly validationErrors: DataModelValidationError[];
  constructor(errors: DataModelValidationError[]) {
    super('Die Testdaten konnten nicht geladen werden.');
    this.name = 'DataModelError';
    this.validationErrors = errors;
  }
}
